import {
  useLayoutEffect,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  memo,
  useMemo
} from 'react'
import { Virtuoso } from 'react-virtuoso'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import SidenavIcon from '../components/icons/sidebarleft.svg?react'
import ChatBubble from '../components/chat/ChatBubble.jsx'
import { useChatNavigation } from '../hooks/useChatNavigation.js'
import ChatNavigationKeybindings from '../components/ChatNavigationKeybindings.jsx'
import { AppContext, MessagesContext } from '../components/AppContext.tsx'
import { useTabContext } from '../components/TabContext.tsx'
import ToolApprovalPopup from '../components/ToolApprovalPopup.jsx'
import MainInput from '../components/MainInput.jsx'
import NewChatOptions from '../components/NewChatOptions.jsx'
import { SkeletonMessage } from '../components/Skeleton.jsx'
import { validateApiKeys } from '../utils/apiKeyValidation.js'
import { useToolApproval, formatToolApproval } from '../hooks/useToolApproval'
import { apiClient } from '../lib/apiClient'
import { useCrypto } from '../hooks/useCrypto.ts'
import { useAgentLoop } from '../hooks/useAgentLoop'
import { appStorage } from '../lib/appStorage'
import { messageService } from '../services/messageService'
import { useMediaQuery } from '../utils/useMediaQuery.js'
import { createSSEStreamParser } from '../utils/sseStreamParser.js'
import { AssistantMessage, UserMessage, Role, Message, MessagePart, VALID_ASSISTANT_PART_TYPES } from '../types/message.ts'

const MessageItem = memo(({ message, index, isStreaming, isMessageFocused, isDark, isLast, isGenerating, onRetry }) => {
  return (
    <div data-msg-index={index}>
      <ChatBubble
        key={message.id}
        message={message}
        index={index}
        files={message.files}
        isStreaming={isStreaming}
        isFocused={isMessageFocused(index)}
        isDark={isDark}
        isLast={isLast}
        isGenerating={isGenerating}
        onRetry={onRetry}
      />
    </div>
  );
});


const ConversationPage = () => {
  const {
    supabaseClient,
    selectedModel,
    files,
    selectedConversation,
    thinkingMode,
    preferAnthropicWebSearch,
    setSelectedConversation,
    input,
    onInputChange,
    menuIsOpen,
    onMenuIsOpenChange,
    resetFiles,
    authSession,
    onAuthSessionUpdate,
    apiKeys,
    favoriteConversations,
    setFavoriteConversations,
    conversations,
    setConversations,
    loadingConversation,
    isGenerating,
    setIsGenerating,
    setActiveConversationTokenCount,
    syncConversationsWithServer,
    isDark,
    systemPrompt,
    tabs,
    createTab,
    updateTab: updateTabById, // Get updateTab from AppContext (takes tabId as first param)
    fetchConversationMessages,
  } = useContext(AppContext)

  // Get active tab from TabContext (for reading current tab state)
  const { tab } = useTabContext()

  // Alias for updating the current active tab
  const updateTab = useCallback((updates: Partial<typeof tab>) => {
    if (tab?.id) {
      updateTabById(tab.id, updates)
    }
  }, [tab?.id, updateTabById])

  const messagesContext = useContext(MessagesContext)
  const { isUnlocked, encrypt, encryptObject } = useCrypto()

  // Tab state (tabs are always initialized - fallback to global state only before tab init)
  const messages = tab?.messages ?? messagesContext.messages
  const effectiveFiles = tab?.files ?? files
  const effectiveInput = tab?.input ?? input
  const effectiveThinkingMode = tab?.thinkingMode ?? thinkingMode
  // Web search preference is now global only (not per-tab)
  // Destructure from context directly in the send function
  const effectiveSystemPrompt = tab?.systemPrompt ?? systemPrompt
  const effectiveIsGenerating = tab?.isGenerating ?? isGenerating
  const effectiveConversationId = tab ? tab.conversationId : selectedConversation?.conversationId

  // Tab is loading if it has a conversationId but no messages yet (fetch in progress)
  const isTabLoadingMessages = Boolean(tab?.conversationId && messages.length === 0 && !effectiveIsGenerating)

  // Ref to track current messages for streaming updates (avoids stale closure)
  const messagesRefForStreaming = useRef<Message[]>(messages)
  useEffect(() => {
    messagesRefForStreaming.current = messages
  }, [messages])

  // Ref for files to avoid re-creating sendMessage on every file change
  const effectiveFilesRef = useRef(effectiveFiles)
  effectiveFilesRef.current = effectiveFiles

  // Per-tab AbortControllers so concurrent streams don't cancel each other
  const streamAbortMapRef = useRef<Map<string, AbortController>>(new Map())

  // Tab-aware setters that maintain backward compatibility
  // IMPORTANT: For tab mode, we use messagesRefForStreaming.current to get the latest messages
  // This avoids stale closure issues during SSE streaming where tab.messages would be outdated
  const setMessages = useCallback((updater: React.SetStateAction<Message[]>) => {
    if (tab) {
      const prev = messagesRefForStreaming.current || []
      const next = typeof updater === 'function'
        ? (updater as (prev: Message[]) => Message[])(prev)
        : updater
      updateTab({ messages: next })
    } else {
      messagesContext.setMessages(updater)
    }
  }, [tab, updateTab, messagesContext])

  const setEffectiveInput = useCallback((valueOrUpdater: string | ((prev: string) => string)) => {
    if (tab) {
      const prev = tab.input ?? ''
      const next = typeof valueOrUpdater === 'function'
        ? (valueOrUpdater as (p: string) => string)(prev)
        : valueOrUpdater
      updateTab({ input: next })
    } else {
      const next = typeof valueOrUpdater === 'function'
        ? (valueOrUpdater as (p: string) => string)(input)
        : valueOrUpdater
      onInputChange(next)
    }
  }, [tab, updateTab, input, onInputChange])

  const resetEffectiveFiles = useCallback(() => {
    if (tab) {
      updateTab({ files: [] })
    } else {
      resetFiles()
    }
  }, [tab, updateTab, resetFiles])

  const setEffectiveIsGenerating = useCallback((value: boolean) => {
    if (tab) {
      updateTab({ isGenerating: value })
    } else {
      setIsGenerating(value)
    }
  }, [tab, updateTab, setIsGenerating])



  // Cleanup: abort all in-flight streams when component unmounts
  useEffect(() => {
    return () => {
      streamAbortMapRef.current.forEach(controller => controller.abort())
      streamAbortMapRef.current.clear()
    }
  }, [])

  // Abort orphaned streams when tabs are closed
  // Derive a stable string key from tab IDs so this only fires when tabs are added/removed
  const tabIdKey = tabs.map(t => t.id).join(',')
  const tabIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const currentTabIds = new Set(tabIdKey.split(',').filter(Boolean))

    // Find stream keys that no longer have a corresponding tab
    streamAbortMapRef.current.forEach((controller, streamKey) => {
      if (streamKey !== '__global__' && !currentTabIds.has(streamKey)) {
        controller.abort()
        streamAbortMapRef.current.delete(streamKey)
      }
    })

    // Also abort orphaned agent loops for closed tabs
    agentLoopIdByTabRef.current.forEach((loopId, tabId) => {
      if (!currentTabIds.has(tabId)) {
        agentLoop?.abortLoop(loopId)
        agentLoopIdByTabRef.current.delete(tabId)
      }
    })

    tabIdsRef.current = currentTabIds
  }, [tabIdKey])

  // Load messages when switching to a tab that has a conversationId but no messages
  useEffect(() => {
    if (!tab || !authSession) return;

    // If tab has a conversationId but no messages, load them (with decryption)
    if (tab.conversationId && tab.messages.length === 0) {
      const loadMessages = async () => {
        try {
          const fetchedMessages = await fetchConversationMessages(tab.conversationId);
          updateTab({ messages: fetchedMessages });
          // Scroll to bottom after loading conversation history
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (virtuosoRef.current && fetchedMessages.length > 0) {
                virtuosoRef.current.scrollToIndex({
                  index: fetchedMessages.length - 1,
                  align: 'end',
                });
              }
            }, 100);
          });
        } catch (error) {
          console.error('Error loading messages for tab:', error);
        }
      };
      loadMessages();
    }
  }, [tab?.id, tab?.conversationId, tab?.messages.length, authSession, updateTab, fetchConversationMessages])

  // Agent loop state (Electron only) — persisted via appStorage
  const [workingDirectory, setWorkingDirectory] = useState<string | null>(null);

  useEffect(() => {
    appStorage.getItem<string>('workingDirectory').then(v => { if (v) setWorkingDirectory(v); });
  }, []);

  // Per-tab streaming state — derived from tab.isGenerating rather than a shared boolean
  // This ensures concurrent streams in different tabs don't interfere with each other
  const streaming = effectiveIsGenerating
  const [isDragging, setIsDragging] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true)

  const [shouldScroll, setShouldScroll] = useState(false)

  // Agent loop hook — supports multiple concurrent loops keyed by loopId
  const agentLoop = useAgentLoop();

  // Track active agent loop IDs per tab for abort support
  const agentLoopIdByTabRef = useRef<Map<string, string>>(new Map());

  const [footerHeight, setFooterHeight] = useState(800);
  const lastMessageRef = useRef(null);

  const isWideScreen = useMediaQuery("(min-width: 1152px)");

  // Tool approval modal state (Electron agent loop)
  const toolApproval = useToolApproval();
  const approvalInfo = toolApproval.pending
    ? formatToolApproval(toolApproval.pending.tool, toolApproval.pending.input)
    : null;

  const updateFooterHeight = useCallback(() => {
    if (lastMessageRef.current) {
      const mainInputHeight = 94;
      const messageHeight = lastMessageRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      const newFooterHeight = Math.max(0, viewportHeight - messageHeight - 60);

      if (newFooterHeight > mainInputHeight) {
        setFooterHeight(prev => newFooterHeight - mainInputHeight);
      } else {
        setFooterHeight(prev => newFooterHeight);
      }
    }
  }, [])

  // Callback ref = handles initial mount
  const setLastMessageRef = useCallback((node) => {
    lastMessageRef.current = node;
    if (node) {
      updateFooterHeight(); // Measure on mount
    }
  }, [updateFooterHeight])

  useLayoutEffect(() => {
    let resizeObserver = null;
    const currentElement = lastMessageRef.current;

    if (streaming && currentElement) {
      resizeObserver = new ResizeObserver(updateFooterHeight);
      resizeObserver.observe(currentElement);
    }

    window.addEventListener('resize', updateFooterHeight);

    if (!streaming) {
      updateFooterHeight()
    }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateFooterHeight);
    }
  }, [streaming, updateFooterHeight])

  // Refs - declare before using in hooks
  const appRef = useRef(null)
  const virtuosoRef = useRef(null)
  const messagesRef = useRef(messages); // Track current messages for positioning

  // Keep messages ref updated
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Scroll to bottom when switching tabs
  useEffect(() => {
    if (!tab?.id || !virtuosoRef.current) return;
    const timer = setTimeout(() => {
      const msgs = messagesRefForStreaming.current;
      if (msgs.length > 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: msgs.length - 1,
          align: 'end',
          behavior: 'auto'
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [tab?.id]);

  const scrollLastMessageToTop = useCallback(() => {
    if (virtuosoRef.current && messages.length > 0) {
      // Find the last user message
      let lastMsgIndex = messages.length - 1;
      if (messages[lastMsgIndex].role !== "user") {
        lastMsgIndex = messages.length - 2;
      }

      // Now we check if the found message, also just so happens
      // to be the first message, because we don't need any scrolling
      // then.
      if (lastMsgIndex === 0) return;

      // Ensure valid index
      if (lastMsgIndex >= 0 && messages[lastMsgIndex]) {
        virtuosoRef.current.scrollToIndex({
          index: lastMsgIndex,
          align: 'start',
          behavior: 'smooth'
        });
      }
    }
  }, [messages])

  // Reference variable for scrolling behaviour
  useEffect(() => {
    if (!shouldScroll) return;

    // Wait for:
    // 1. React to render the new message
    // 2. Virtuoso to compute layout
    // 3. Footer ResizeObserver to fire and update height
    const scrollTimer = setTimeout(() => {
      scrollLastMessageToTop();
      setShouldScroll(false);
    }, 150); // Increased delay to allow all updates to complete

    return () => clearTimeout(scrollTimer);
  }, [shouldScroll, scrollLastMessageToTop]); // Remove 'messages' dependency to avoid multiple triggers

  // Chat navigation hook
  const chatNavigation = useChatNavigation(messages, virtuosoRef);
  const {
    isMessageFocused,
  } = chatNavigation;

  // Smooth scroll functions for navigation
  const scrollDown = useCallback(() => {
    const ref = virtuosoRef.current;
    if (ref) {
      ref.scrollBy({ top: 200, behavior: 'smooth' });
    }
  }, []);

  const scrollUp = useCallback(() => {
    const ref = virtuosoRef.current;
    if (ref) {
      ref.scrollBy({ top: -200, behavior: 'smooth' });
    }
  }, []);

  // Stop the current generation (abort in-flight stream or agent loop)
  const stopGeneration = useCallback(() => {
    const streamKey = tab?.id ?? '__global__';
    const controller = streamAbortMapRef.current.get(streamKey);
    if (controller) {
      controller.abort();
    }
    if (agentLoop && tab?.id) {
      const loopId = agentLoopIdByTabRef.current.get(tab.id);
      if (loopId) {
        agentLoop.abortLoop(loopId);
        agentLoopIdByTabRef.current.delete(tab.id);
      }
    } else if (agentLoop) {
      agentLoop.abortLoop();
    }
  }, [tab?.id, agentLoop]);

  // Virtuoso handles scroll differently - we don't need manual scroll listener
  const handleAtBottomStateChange = useCallback((atBottom) => {
    setIsScrolledToBottom(atBottom);
  }, []);

  // Check for an existing session and set up auth state listener
  // Using a separate ref to avoid duplicate auth updates
  const authInitializedRef = useRef(false)

  useEffect(() => {
    // Skip if we've already initialized auth
    if (authInitializedRef.current) {
      return
    }

    const initializeAuth = async () => {
      // Mark as initialized to prevent duplicate calls
      authInitializedRef.current = true

      // Check for an existing session
      try {
        const { data: { session } } = await supabaseClient.auth.getSession()
        if (session?.user) {
          onAuthSessionUpdate(session)
        }
      } catch (err) {
        // Reset flag to allow retry
        authInitializedRef.current = false
      }
    }

    initializeAuth()

    // Set up auth state change listener (separate from initial check)
    const { data: listener } = supabaseClient.auth.onAuthStateChange((event, session) => {
      // Only update if the state is different
      if (event !== 'INITIAL_SESSION') {
        onAuthSessionUpdate(session ?? null)
      }
    })

    return () => {
      listener.subscription.unsubscribe()
    }
  }, [supabaseClient, onAuthSessionUpdate])

  // Setup file drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  // Attach drag and drop listeners
  useEffect(() => {
    const draggable = appRef.current
    if (draggable) {
      draggable.addEventListener('dragover', handleDragEnter)
      draggable.addEventListener('dragleave', handleDragLeave)
      draggable.addEventListener('drop', handleDrop)
    }

    return () => {
      const element = appRef.current
      if (element) {
        element.removeEventListener('dragover', handleDragEnter)
        element.removeEventListener('dragleave', handleDragLeave)
        element.removeEventListener('drop', handleDrop)
      }
    }
  }, [handleDragEnter, handleDragLeave, handleDrop])

  // No automatic scrolling during messages updates - user controls their reading experience

  // Use ref to track last known state to avoid unnecessary updates
  const lastKnownStateRef = useRef({
    messages: [],
    streaming: false,
    conversationId: null
  });

  // Update conversation in storage when messages change (but debounced)
  // Using a ref to track if we're currently updating to break circular dependencies
  const isUpdatingRef = useRef(false);

  // We want to debounce/throttle the storage updates, so use a timeout ref
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    // Tab mode handles its own state — skip legacy global sync
    if (tab) return;

    // Skip if we're already in the middle of an update
    if (isUpdatingRef.current) return;

    // Skip during streaming to avoid constant updates
    if (streaming) return;

    // Skip if no messages (empty conversation)
    if (!messages.length) return;

    // Skip if nothing has actually changed to prevent loops
    const lastState = lastKnownStateRef.current;
    if (
      lastState.conversationId === selectedConversation.conversationId &&
      lastState.messages.length === messages.length &&
      JSON.stringify(lastState.messages) === JSON.stringify(messages)
    ) {
      return;
    }

    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce the update to avoid rapid successive changes
    updateTimeoutRef.current = setTimeout(() => {
      // Mark that we're updating to prevent loops
      isUpdatingRef.current = true;

      try {
        // Update the ref with current state
        lastKnownStateRef.current = {
          messages: [...messages],
          streaming,
          conversationId: selectedConversation.conversationId
        };

        // Only update conversations in storage for those with a server ID
        if (selectedConversation.conversationId) {
          if (selectedConversation.favorite) {
            setFavoriteConversations(prevConversations => {
              const existingIndex = prevConversations.findIndex(c => c.conversationId === selectedConversation.conversationId);

              if (existingIndex >= 0) {
                const updatedConversations = [...prevConversations];
                updatedConversations[existingIndex] = {
                  ...updatedConversations[existingIndex],
                  history: messages
                };
                return updatedConversations;
              } else if (selectedConversation.conversationId) {
                return [...prevConversations, {
                  conversationId: selectedConversation.conversationId,
                  favorite: true,
                  history: messages
                }];
              }

              return prevConversations;
            });
          } else {
            setConversations(prevConversations => {
              const existingIndex = prevConversations.findIndex(c => c.conversationId === selectedConversation.conversationId);

              if (existingIndex >= 0) {
                const updatedConversations = [...prevConversations];
                updatedConversations[existingIndex] = {
                  ...updatedConversations[existingIndex],
                  history: messages
                };
                return updatedConversations;
              } else if (selectedConversation.conversationId) {
                return [...prevConversations, {
                  conversationId: selectedConversation.conversationId,
                  favorite: false,
                  history: messages
                }];
              }

              return prevConversations;
            });
          }

        }
      } finally {
        // Reset the updating flag when done
        isUpdatingRef.current = false;
        updateTimeoutRef.current = null;
      }
    }, 300); // Debounce for 300ms

    // Cleanup function to clear the timeout if the component unmounts
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [tab, messages, streaming, selectedConversation.conversationId,
    selectedConversation.favorite, setConversations, setFavoriteConversations
  ])

  // Track the previous conversation ID to detect real changes
  const prevConversationIdRef = useRef(null);

  // Track if we just ended streaming
  const justEndedStreamingRef = useRef(false);

  // Watch streaming state changes
  useEffect(() => {
    if (!streaming) {
      // Mark that we just ended streaming to prevent message override
      justEndedStreamingRef.current = true;

      // Reset the flag after a brief delay
      const timeoutId = setTimeout(() => {
        justEndedStreamingRef.current = false;
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [streaming]);

  // Update messages when selected conversation changes
  useEffect(() => {
    // Skip if we're already in the middle of an update
    if (isUpdatingRef.current) return;

    // Skip if we're currently streaming to avoid losing messages
    if (streaming) return;

    // Skip if we just ended streaming - this prevents message loss after first conversation
    if (justEndedStreamingRef.current) {
      return;
    }

    // Check if the conversation has actually changed
    const hasConversationChanged = prevConversationIdRef.current !== selectedConversation.conversationId;

    // Update our ref with the current ID
    prevConversationIdRef.current = selectedConversation.conversationId;

    // Only perform update if the conversation has changed
    if (hasConversationChanged) {
      // Set a flag to avoid circular updates
      isUpdatingRef.current = false;
    }
  }, [selectedConversation.conversationId, selectedConversation.history, streaming])

  // This unused event handler section has been removed

  // Convert files to data URLs for display
  const readFilesAsDataUrls = useCallback(async (files) => {
    try {
      const filePromises = files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            resolve({
              content: reader.result,
              name: file.file.name,
              type: "image"
            })
          }
          reader.onerror = (error) => reject(error)
          reader.readAsDataURL(file.file)
        })
      })

      return await Promise.all(filePromises)
    } catch (error) {
      return []
    }
  }, [])

  // Position user message optimally for streaming context
  // Removed positionForOptimalStreaming - now handled by useLayoutEffect

  // Send a message and handle the SSE response
  // IMPORTANT: This function captures the tab ID at the start and creates a per-stream
  // setMessages that always writes to that specific tab, even if user switches tabs mid-stream
  const sendMessage = useCallback(async (userInput) => {
    if (!authSession) return;

    // Validate we have required API keys for the selected model
    if (!validateApiKeys(apiKeys, selectedModel?.model_family)) {
      return;
    }

    // ===== CAPTURE TAB CONTEXT AT SEND TIME =====
    // This ensures streaming updates go to the correct tab even if user switches tabs
    const targetTabId = tab?.id ?? null;
    const streamKey = targetTabId ?? '__global__';

    // Cancel any in-flight stream for THIS tab only (other tabs keep streaming)
    const existingController = streamAbortMapRef.current.get(streamKey);
    if (existingController) {
      existingController.abort();
    }
    const abortController = new AbortController();
    streamAbortMapRef.current.set(streamKey, abortController);
    const baseMessages: Message[] = targetTabId
      ? (tabs.find(t => t.id === targetTabId)?.messages ?? [])
      : messages;
    const wasNewConversation = !effectiveConversationId;

    // Generate IDs using UUIDs to match backend format
    const userId = crypto.randomUUID();
    const sysId = crypto.randomUUID();

    // Process files (use ref to avoid dependency on effectiveFiles)
    const currentFiles = effectiveFilesRef.current;

    // In Electron with workspace: split files into images (base64 to LLM) and uploads (save to disk)
    const isWorkspaceMode = Boolean(workingDirectory && window.electronAPI?.workspace);
    const imageFiles = isWorkspaceMode
      ? currentFiles.filter(f => f.file.type.startsWith('image/'))
      : currentFiles;
    const uploadFiles = isWorkspaceMode
      ? currentFiles.filter(f => !f.file.type.startsWith('image/'))
      : [];

    // Read image files as data URLs for the LLM vision pipeline
    const readFiles = await readFilesAsDataUrls(imageFiles);

    // Save non-image files to uploads/ and build attachment references
    let attachmentPreamble = '';
    if (uploadFiles.length > 0 && workingDirectory) {
      try {
        const uploadResults = await Promise.all(
          uploadFiles.map(async (f) => {
            return (window as any).electronAPI.workspace.saveUpload(f.file.name, f.file.path);
          }),
        );
        attachmentPreamble = uploadResults
          .map((r: any, i: number) => {
            const sizeStr = r.size < 1024 ? `${r.size} B`
              : r.size < 1024 * 1024 ? `${(r.size / 1024).toFixed(1)} KB`
              : `${(r.size / (1024 * 1024)).toFixed(1)} MB`;
            const mimeType = uploadFiles[i]?.file.type || 'application/octet-stream';
            return `[File attached: ${r.logicalPath} (${sizeStr}, ${mimeType})]`;
          })
          .join('\n');

        // Also save images to uploads/ so the agent can reference them from disk later
        if (imageFiles.length > 0) {
          await Promise.all(
            imageFiles.map(async (f) => {
              return (window as any).electronAPI.workspace.saveUpload(f.file.name, f.file.path);
            }),
          );
        }
      } catch (err: any) {
        toast.error('Failed to save attachments: ' + (err.message || 'Unknown error'));
        return;
      }
    }

    // Build the effective content: attachment references + user text
    const effectiveContent = [attachmentPreamble, userInput].filter(Boolean).join('\n\n');

    // Add user message
    const userMessage: UserMessage = {
      id: userId,
      conversation_id: effectiveConversationId || '',
      role: Role.user,
      content: effectiveContent,
      created_at: new Date().toISOString(),
      message_parts: null,
      files: readFiles,
      metadata: {
        images: readFiles.map(file => ({ url: file.content })),
      }
    };

    // Add assistant message placeholder
    const assistantMessage: AssistantMessage = {
      id: sysId,
      conversation_id: effectiveConversationId || '',
      role: Role.assistant,
      content: null,
      created_at: new Date().toISOString(),
      message_parts: [],
      metadata: {}
    };

    // Initial messages with user + assistant placeholder
    const initialStreamMessages = [...baseMessages, userMessage, assistantMessage];

    // ===== CREATE PER-STREAM MESSAGE ID REF =====
    // IMPORTANT: Each stream needs its own message ID ref to avoid conflicts
    // when multiple tabs are streaming simultaneously. If we used a shared ref,
    // Tab B's message would overwrite Tab A's ID, causing Tab A to lose updates.
    const streamMessageIdRef = { current: sysId };

    // ===== CREATE PER-STREAM MESSAGES SETTER =====
    // This maintains a local buffer and always writes to the captured tab
    let streamMessages = initialStreamMessages;

    const setMessagesForStream = (updater: React.SetStateAction<Message[]>) => {
      const next = typeof updater === 'function'
        ? (updater as (prev: Message[]) => Message[])(streamMessages)
        : updater;

      streamMessages = next; // Keep local buffer in sync

      if (targetTabId) {
        // Write only to the original tab, even if user switches tabs later
        updateTabById(targetTabId, { messages: next });
      } else {
        // Legacy single-tab path
        messagesContext.setMessages(next);
      }
    };

    // Push initial messages to the correct tab/global state
    setMessagesForStream(initialStreamMessages);
    setShouldScroll(true);

    // Set the UI to a generating state (for the captured tab)
    if (targetTabId) {
      updateTabById(targetTabId, { isGenerating: true });
    } else {
      setIsGenerating(true);
    }

    // ===== SHARED REQUEST PAYLOAD =====
    const history = baseMessages.map((x: Message) => {
      if (x.role === Role.user) {
        return {
          role: x.role,
          message_parts: [{ type: 'text', content: x.content }],
        };
      }
      const safeParts = Array.isArray(x.message_parts)
        ? x.message_parts.filter((p: any) => VALID_ASSISTANT_PART_TYPES.has(p?.type))
        : [];
      return {
        role: x.role,
        message_parts: safeParts,
      };
    });

    const inputMessages: any[] = [];
    if (effectiveContent) {
      inputMessages.push({ type: 'text', content: { text: effectiveContent } });
    }
    imageFiles.forEach(file => {
      const imgId = `${file.id}${file.file.name}`;
      inputMessages.push({ type: 'image', content: { image: imgId } });
    });

    const requestPayload = {
      model_id: selectedModel.id,
      messages: history,
      new_messages: inputMessages,
      system_prompt: `Current local time: ${new Date().toLocaleString()}\n\n` + effectiveSystemPrompt,
      settings: { thinking_mode: effectiveThinkingMode },
      api_keys: {
        anthropic_api_key: apiKeys.anthropicApiKey,
        openai_api_key: apiKeys.openAIApiKey,
        gemini_api_key: apiKeys.geminiApiKey,
        xai_api_key: apiKeys.xaiApiKey,
        openrouter_api_key: apiKeys.openrouterApiKey,
      },
      conversation_id: effectiveConversationId,
      prefer_anthropic_web_search: preferAnthropicWebSearch,
    };

    setEffectiveInput('');
    resetEffectiveFiles();
    const restoreInput = () => setEffectiveInput(userInput);

    // ===== BRANCH: AGENT LOOP (Electron with filesystem tools) VS SSE =====
    const useAgentLoopPath = agentLoop && workingDirectory;

    let streamConversationId: string | null = effectiveConversationId || null;

    // Helper to persist user message to DB before streaming
    const storeUserMessageToDB = async (conversationId: string) => {
      try {
        const shouldEncrypt = isUnlocked;
        let userContent: string;
        let userMetadata: string | undefined;

        const userMeta = userMessage.metadata || {};
        const hasUserMeta = userMeta.images?.length > 0;

        if (shouldEncrypt) {
          userContent = await encrypt(userMessage.content || '');
          if (hasUserMeta) {
            userMetadata = await encryptObject(userMeta);
          }
        } else {
          userContent = userMessage.content || '';
          if (hasUserMeta) {
            userMetadata = JSON.stringify(userMeta);
          }
        }

        await messageService.storeMessages(conversationId, [
          messageService.createUserMessage(userContent, shouldEncrypt, userMetadata),
        ]);
      } catch (err) {
        console.error('Failed to pre-store user message:', err);
      }
    };

    try {
      if (useAgentLoopPath) {
        // === ELECTRON AGENT LOOP PATH ===
        // The main process owns the multi-turn loop and executes filesystem tools locally.

        const accessToken = await apiClient.ensureFreshToken();
        const { toolDefinitions } = await import('../../electron/tools/toolDefinitions.js');

        // Generate conversation ID if needed and save it
        const agentLoopConversationId = effectiveConversationId || crypto.randomUUID();
        streamConversationId = agentLoopConversationId;

        // Update tab state BEFORE starting the loop so subsequent messages reuse this ID
        if (targetTabId && agentLoopConversationId) {
          const knownTitle = conversations.find(c => c.conversationId === agentLoopConversationId)?.title ||
            favoriteConversations.find(c => c.conversationId === agentLoopConversationId)?.title;
          updateTabById(targetTabId, {
            conversationId: agentLoopConversationId,
            ...(knownTitle ? { title: knownTitle } : {}),
          });
        }
        if (agentLoopConversationId) {
          setSelectedConversation({ conversationId: agentLoopConversationId, history: initialStreamMessages });
          setConversations(prev => {
            const index = prev.findIndex(c => c.conversationId === agentLoopConversationId);
            const updated = index >= 0 ? [...prev] : [...prev, { conversationId: agentLoopConversationId, history: [] }];
            updated[index >= 0 ? index : updated.length - 1] = {
              conversationId: agentLoopConversationId,
              history: initialStreamMessages
            };
            return updated;
          });
        }

        // Ensure conversation exists on the server before storing the user message
        // (llm/stream normally creates it, but we need it now for resilience)
        if (!effectiveConversationId) {
          await apiClient.post('/api/conversations', {
            conversation_id: agentLoopConversationId,
          });
        }

        await storeUserMessageToDB(agentLoopConversationId);

        // Serialize image files for IPC (File objects can't cross the IPC boundary)
        const serializedFiles = await Promise.all(
          imageFiles.map(async (f) => {
            const arrayBuffer = await f.file.arrayBuffer();
            const imgId = `${f.id}${f.file.name}`;
            return {
              name: imgId,
              data: new Uint8Array(arrayBuffer),
              contentType: f.file.type,
            };
          }),
        );

        const { loopId, completion } = await agentLoop.startLoop({
          ...requestPayload,
          newMessages: requestPayload.new_messages,
          tools: toolDefinitions,
          model: {
            id: selectedModel.id,
            name: selectedModel.name,
            display_name: selectedModel.display_name,
            model_family: selectedModel.model_family,
            capabilities: selectedModel.capabilities,
          },
          systemPrompt: requestPayload.system_prompt,
          apiKeys: requestPayload.api_keys,
          conversationId: agentLoopConversationId,
          webSearchEnabled: requestPayload.prefer_anthropic_web_search,
          cwd: workingDirectory,
          accessToken,
          files: serializedFiles.length > 0 ? serializedFiles : null,
        }, {
          setMessages: setMessagesForStream,
          messageId: sysId,
        });

        // Track loopId immediately for abort support
        if (targetTabId) {
          agentLoopIdByTabRef.current.set(targetTabId, loopId);
        }

        // Wait for generation to complete
        await completion;
      } else {
        // === SSE PATH (Web + Electron without filesystem tools) ===
        // Single POST to /api/llm/stream, parse SSE response client-side.
        const parseSSEStream = createSSEStreamParser({
          setMessages: setMessagesForStream,
          messageIdRef: streamMessageIdRef,
        });

        const formData = new FormData();
        imageFiles.forEach(file => {
          const imgId = `${file.id}${file.file.name}`;
          formData.append('files', file.file, imgId);
        });
        formData.append('jsonData', JSON.stringify(requestPayload));

        const response = await apiClient.authenticatedRequest('/api/llm/stream', {
          method: 'POST',
          body: formData,
          signal: abortController.signal,
        });

        if (!response.ok) {
          let errorMessage = 'Unable to establish SSE stream';
          try {
            const errorData = await response.json();
            if (errorData.detail) errorMessage = errorData.detail;
          } catch {}
          throw new Error(errorMessage);
        }
        if (!response.body) throw new Error('ReadableStream not supported');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const conversationId = response.headers.get('X-Conversation-ID');
        streamConversationId = conversationId;

        if (conversationId) {
          if (targetTabId) {
            const knownTitle = conversations.find(c => c.conversationId === conversationId)?.title ||
              favoriteConversations.find(c => c.conversationId === conversationId)?.title;
            updateTabById(targetTabId, {
              conversationId,
              ...(knownTitle ? { title: knownTitle } : {}),
            });
          }
          setSelectedConversation({ conversationId, history: initialStreamMessages });
          setConversations(prev => {
            const index = prev.findIndex(c => c.conversationId === conversationId);
            const updated = index >= 0 ? [...prev] : [...prev, { conversationId, history: [] }];
            updated[index >= 0 ? index : updated.length - 1] = {
              conversationId,
              history: initialStreamMessages
            };
            return updated;
          });
        }

        if (conversationId) {
          await storeUserMessageToDB(conversationId);
        }

        await parseSSEStream(reader, decoder, restoreInput);
      }

      // === SHARED POST-STREAM LOGIC ===
      streamAbortMapRef.current.delete(streamKey);
      if (targetTabId) {
        agentLoopIdByTabRef.current.delete(targetTabId);
      }

      if (targetTabId) {
        updateTabById(targetTabId, { isGenerating: false });
      } else {
        setIsGenerating(false);
      }

      // Store assistant message client-side after stream completes (user msg already stored)
      if (streamConversationId) {
        try {
          const finalAssistantMsg = streamMessages.find(m => m.id === sysId);

          if (finalAssistantMsg) {
            const shouldEncrypt = isUnlocked;
            let assistantContent: string;

            if (shouldEncrypt) {
              assistantContent = await encryptObject({
                message_parts: finalAssistantMsg.message_parts || [],
                metadata: finalAssistantMsg.metadata || {},
              });
            } else {
              assistantContent = JSON.stringify({
                message_parts: finalAssistantMsg.message_parts || [],
                metadata: finalAssistantMsg.metadata || {},
              });
            }

            await messageService.storeMessages(streamConversationId, [
              messageService.createAssistantMessage(assistantContent, undefined, shouldEncrypt),
            ]);

            const isNewConv = !conversations.some(c => c.conversationId === streamConversationId);
            if (isNewConv) {
              const plainTitle = (userInput || 'New conversation').slice(0, 48).trim();
              const title = shouldEncrypt ? await encrypt(plainTitle) : plainTitle;

              await apiClient.put(`/api/conversations/${streamConversationId}/title`, {
                title,
                encrypted: shouldEncrypt,
              });
            }
          }
        } catch (err) {
          console.error('Failed to store assistant message:', err);
          toast.error('Failed to save response. Your messages may not be saved.');
        }
      }

      // Sync conversations if this was a new conversation
      const isNewConversation = !conversations.some(c => c.conversationId === streamConversationId);
      if (isNewConversation) {
        setTimeout(async () => {
          try {
            await syncConversationsWithServer();
          } catch (error) {
            console.warn('Failed to sync conversations:', error);
          }
        }, 2000);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        streamAbortMapRef.current.delete(streamKey);
        if (targetTabId) {
          updateTabById(targetTabId, { isGenerating: false });
        } else {
          setIsGenerating(false);
        }
        return;
      }

      if (error.renderMsg) {
        toast.error(error.renderMsg);
      } else {
        toast.error(error.message || 'An error occurred while processing your message.');
      }

      restoreInput();
      streamAbortMapRef.current.delete(streamKey);

      if (targetTabId) {
        updateTabById(targetTabId, { isGenerating: false });
      } else {
        setIsGenerating(false);
      }

      // Revert state for new conversations that failed
      if (streamConversationId && wasNewConversation) {
        if (targetTabId) {
          updateTabById(targetTabId, { conversationId: null, title: 'New Chat' });
        }
        setSelectedConversation({ conversationId: null, history: [] });
        setConversations(prev => prev.filter(c => c.conversationId !== streamConversationId));
      }
    }
  }, [
    authSession, messages, tabs, effectiveSystemPrompt, effectiveThinkingMode,
    selectedModel, apiKeys, setEffectiveInput, resetEffectiveFiles, readFilesAsDataUrls,
    conversations, favoriteConversations, syncConversationsWithServer,
    effectiveConversationId, setConversations, setIsGenerating,
    setSelectedConversation, tab, updateTabById, messagesContext,
    isUnlocked, encrypt, encryptObject,
    agentLoop, workingDirectory
  ]);

  const retryLastMessage = useCallback(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role !== Role.user || !lastMsg.content) return;

    const userContent = lastMsg.content;

    // Delete the persisted user message from the DB
    if (effectiveConversationId) {
      messageService.deleteMessage(effectiveConversationId, lastMsg.id).catch(err => {
        console.error('Failed to delete orphaned user message:', err);
      });
    }

    // Remove from local state
    setMessages(prev => prev.slice(0, -1));

    // Re-send
    sendMessage(userContent);
  }, [messages, effectiveConversationId, setMessages, sendMessage]);


  // UI event handlers
  const toggleMenu = useCallback(() => {
    onMenuIsOpenChange(!menuIsOpen)
  }, [menuIsOpen, onMenuIsOpenChange])

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="flex flex-row no-wrap w-full h-full @container relative">
      <motion.div
        className="flex flex-col items-center justify-between w-full h-full min-h-0 relative px-2"
      >
        {/* Backdrop overlay when approval is pending - covers entire conversation page */}
        {toolApproval.pending && (
          <div className="absolute inset-0 bg-black/20 z-10 pointer-events-none" />
        )}

        {/* SidenavIcon when menu is closed but only on mobile */}
        {!menuIsOpen && isMobile && (
          <button
            tabIndex={1}
            className="absolute top-2 left-2 p-1.5 rounded hover:bg-bg-hover focus:outline-none focus:bg-bg-hover transition-all duration-200 z-20"
            onClick={toggleMenu}
            aria-label="Open menu"
          >
            <SidenavIcon className="size-4 cursor-pointer text-primary" style={{ color: 'var(--color-primary)' }} />
          </button>
        )}
        {messages.length === 0 && !loadingConversation && !isTabLoadingMessages ? (
          <div className="flex flex-col items-center justify-center flex-1 w-full h-full relative">
            {/* NewChatOptions - centered in full viewport height */}
            <div className="flex w-full max-w-[768px] flex-col items-center gap-spacing-2 mb-[96px]">
              <NewChatOptions />
            </div>

            {/* MainInput - absolutely positioned at bottom */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[768px] pb-spacing-2">
              <div className="relative">
                {/* Tool Approval Popup (Electron agent loop) */}
                {toolApproval.pending && (
                  <div className="absolute bottom-full left-0 right-0 pb-3">
                    <ToolApprovalPopup
                      tool={toolApproval.pending.tool}
                      title={approvalInfo?.title || 'Approve tool'}
                      command={approvalInfo?.message || ''}
                      onAllow={toolApproval.approve}
                      onAlwaysAllow={toolApproval.approveAlways}
                      onDeny={toolApproval.deny}
                    />
                  </div>
                )}
                <div className={toolApproval.pending ? 'opacity-60 pointer-events-none' : ''}>
                  <MainInput
                    sendMessage={sendMessage}
                    isDragging={isDragging}
                    isGenerating={effectiveIsGenerating}
                    onStopGeneration={stopGeneration}
                    anchored={true}
                    workingDirectory={workingDirectory}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full w-full max-w-[768px] mx-auto min-w-0">
            {/* Messages Container with Scrolling */}
            <div className="flex-1 min-h-0">
              {loadingConversation || isTabLoadingMessages ? (
                <div className="w-full h-full overflow-y-auto">
                  <div className="animate-fadeIn">
                    <SkeletonMessage isUser={false} />
                    <SkeletonMessage isUser={true} />
                    <SkeletonMessage isUser={false} />
                    <SkeletonMessage isUser={true} />
                  </div>
                </div>
              ) : (
                <Virtuoso
                ref={virtuosoRef}
                data={messages}
                followOutput={false}
                increaseViewportBy={{ top: 300, bottom: 300 }}
                alignToBottom={false}
                className="w-full flex-1 mt-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                atBottomStateChange={handleAtBottomStateChange}
                components={{
                  Header: () => (
                    <div className="pointer-events-none sticky top-0 h-8 w-full bg-gradient-to-b from-bg-panel to-transparent z-10" />
                  ),
                  Footer: () => (
                    <div style={{ height: footerHeight }} aria-hidden="true" />
                  )
                }}
                itemContent={(index, message) => {
                  const lastIndex = messages.length - 1;
                  const isLast = index === lastIndex;

                  return (
                    <div ref={isLast ? setLastMessageRef : null}>
                      <MessageItem
                        key={message.id}
                        message={message}
                        index={index}
                        isStreaming={streaming && isLast}
                        isMessageFocused={isMessageFocused}
                        isDark={isDark}
                        isLast={isLast}
                        isGenerating={effectiveIsGenerating}
                        onRetry={retryLastMessage}
                      />
                    </div>
                  );
                }}

              />
              )}
            </div>

            {/* Input Box - Inside main content */}
            <div className="w-full pb-2" data-input-area>
              <div className="relative">
                {/* Tool Approval Popup (Electron agent loop) */}
                {toolApproval.pending && (
                  <div className="absolute bottom-full left-0 right-0 pb-3">
                    <ToolApprovalPopup
                      tool={toolApproval.pending.tool}
                      title={approvalInfo?.title || 'Approve tool'}
                      command={approvalInfo?.message || ''}
                      onAllow={toolApproval.approve}
                      onAlwaysAllow={toolApproval.approveAlways}
                      onDeny={toolApproval.deny}
                    />
                  </div>
                )}
                <div className={toolApproval.pending ? 'opacity-60 pointer-events-none' : ''}>
                  <MainInput
                  sendMessage={sendMessage}
                  isDragging={isDragging}
                  anchored={true}
                  workingDirectory={workingDirectory}
                  showScrollToBottom={messages.length > 0 && !isScrolledToBottom && !toolApproval.pending}
                  onScrollToBottom={() => {
                    // Scroll to the bottom using Virtuoso's API
                    const ref = virtuosoRef.current;
                    if (ref) {
                      ref.scrollToIndex({
                        index: messages.length - 1,
                        behavior: 'smooth',
                        align: 'start'
                      });
                    }
                  }}
                  isGenerating={effectiveIsGenerating}
                  onStopGeneration={stopGeneration}
                  hasContent={(
                    effectiveIsGenerating &&
                    messages.length > 0 &&
                    messages[messages.length - 1].role !== Role.user &&
                    (
                      messages[messages.length - 1].message_parts?.length > 0
                    )
                  )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Chat Navigation Keybindings - Active when messages exist */}
      {messages.length > 0 && (
        <ChatNavigationKeybindings
          chatNavigation={chatNavigation}
          scrollUp={scrollUp}
          scrollDown={scrollDown}
          messages={messages}
        />
      )}
    </div>
  )
}

export default ConversationPage
